import { de } from '../../i18n/de';
import '../../styles/print.css';
import { BodyFigure } from './BodyFigure';
import {
  KIT_INSTRUCTIONS,
  KIT_RESTORE_STEPS,
  KIT_SECRET_LINES,
  kitAlphabetGroups,
  kitBodySheets,
  kitCoreWords,
  kitNotfallPhrases,
  kitPainLevels,
} from './kitData';

/**
 * The no-tech kit (1.16, ADR-007) — six A4 sheets, printed by Safari's own print dialogue.
 *
 * No PDF library: the browser already renders A4 and already writes PDFs, and every dependency here
 * is a thing that can break the person's voice (`CLAUDE.md`). `?print=kit` shows the sheets,
 * the print button opens the dialogue, and the result is laminated and put in the room.
 *
 * **Its acceptance criterion is physical**: printed, laminated, present with the device. Not a file
 * in a repo — an object in a room. Everything in this file only gets that far if someone prints it.
 *
 * Sheet 6 arrived with Phase 4 (4.8) and is the only one with blanks: the restore procedure, plus lines for
 * the six words and the device key in somebody's handwriting. It is what makes "the passphrase is recorded
 * where the family can find it" a deliverable rather than a note in a ticket.
 */
export function Kit(): React.JSX.Element {
  const bodies = kitBodySheets();

  return (
    <div className="kit" data-testid="kit">
      <div className="kit-no-print" style={{ marginBottom: '4mm', display: 'flex', gap: '3mm' }}>
        <button
          type="button"
          data-testid="kit-print"
          onClick={() => window.print()}
          style={{
            minHeight: 'var(--min-target)',
            paddingInline: 16,
            fontSize: 'var(--font-button)',
            background: 'var(--key-bg)',
            color: 'var(--key-fg)',
            border: '3px solid var(--key-border)',
            borderRadius: 'var(--radius)',
          }}
        >
          Drucken
        </button>
        <p style={{ fontSize: 'var(--font-body)' }}>
          A4, Hochformat, beidseitig nicht nötig. Nach dem Drucken laminieren und zum Gerät legen.
        </p>
      </div>

      {/* 1 — Core board. The Kernvokabular that covers most of everyday speech. */}
      <section className="kit-sheet" data-testid="kit-sheet:core">
        <h1 className="kit-title">Kernwörter</h1>
        <p className="kit-subtitle">
          Zum Zeigen. Funktioniert ohne Strom, ohne Netz und ohne Gerät.
        </p>
        <div className="kit-grid kit-grid--core">
          {kitCoreWords().map((word) => (
            <div key={word} className="kit-cell">
              {word}
            </div>
          ))}
        </div>
        <p className="kit-footer">
          {de.appName} — {de.appTagline}
        </p>
      </section>

      {/* 2 — Notfall. The five phrases the app protects from deletion. */}
      <section className="kit-sheet" data-testid="kit-sheet:notfall">
        <h1 className="kit-title">{de.surfaceNotfall}</h1>
        <p className="kit-subtitle">Zeigen genügt. Bitte laut vorlesen, damit klar ist, was gemeint ist.</p>
        <div className="kit-grid kit-grid--notfall">
          {kitNotfallPhrases().map((phrase) => (
            <div key={phrase.label} className="kit-cell kit-cell--emphasis">
              {phrase.label}
            </div>
          ))}
        </div>
        <p className="kit-footer">
          Der Rufknopf im Zimmer ist ein Knopf, kein Programm. Das Gerät ersetzt ihn nicht.
        </p>
      </section>

      {/* 3 — Pain scale and the body figure. Eleven cells fit across a page; the screen needed two
          rows, and the screen could not carry the figure at all. */}
      <section className="kit-sheet" data-testid="kit-sheet:pain">
        <h1 className="kit-title">Schmerzen</h1>
        <p className="kit-subtitle">Wie stark? Bitte auf eine Zahl zeigen. 0 heisst keine Schmerzen.</p>
        <div className="kit-pain">
          {kitPainLevels().map(({ level, word }) => (
            <div key={level} className="kit-pain-cell">
              {level}
              <span className="kit-pain-word">{word}</span>
            </div>
          ))}
        </div>

        <p className="kit-subtitle" style={{ marginTop: '3mm' }}>
          Wo? Bitte auf die Stelle zeigen.
        </p>
        <div className="kit-bodies">
          {bodies.map((body) => (
            <div key={body.side} className="kit-body">
              <span className="kit-body-label">{body.label}</span>
              <BodyFigure side={body.side} regions={body.regions} />
            </div>
          ))}
        </div>
      </section>

      {/* 4 — Alphabet card. Same groups, same order as the partner-assisted surface. */}
      <section className="kit-sheet" data-testid="kit-sheet:alphabet">
        <h1 className="kit-title">Buchstaben abfragen</h1>
        <p className="kit-subtitle">
          Eine Gruppe laut vorlesen und auf Ja oder Nein warten. Bei Ja die Buchstaben einzeln
          durchgehen. Genau diese Reihenfolge benutzt auch das Gerät.
        </p>
        <div className="kit-groups">
          {kitAlphabetGroups().map((group, index) => (
            <div key={group.id} className="kit-group">
              <span className="kit-group-number">{index + 1}</span>
              <span className="kit-group-letters">
                {group.letters.map((letter) => (
                  <span key={letter}>{letter === ' ' ? '␣' : letter}</span>
                ))}
              </span>
            </div>
          ))}
        </div>
        <p className="kit-footer">␣ heisst Leerzeichen — ein Wort ist zu Ende.</p>
      </section>

      {/* 5 — The instruction sheet. The one most likely to be skipped, and the one that changes the
          conversation most. */}
      <section className="kit-sheet" data-testid="kit-sheet:partner">
        <h1 className="kit-title">Für Gesprächspartner</h1>
        <div className="kit-prose">
          {KIT_INSTRUCTIONS.map((section) => (
            <div key={section.heading}>
              <h2>{section.heading}</h2>
              <ul>
                {section.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="kit-footer">
          {de.appName} — {de.appTagline}
        </p>
      </section>

      {/* 6 — The backup card (4.8). The only sheet with something to fill in by hand.

          Deliberately not printed with the secrets on it. On iPadOS the print dialogue is one tap from
          "Save to PDF", which lands in Files and therefore in iCloud — a printed passphrase becomes a
          passphrase in cloud storage, which is the one thing this whole design refuses. It also matches what
          the app is able to do: the six words are never stored, so it could not reprint them later. */}
      <section className="kit-sheet" data-testid="kit-sheet:backup">
        <h1 className="kit-title">Wiederherstellen</h1>
        <p className="kit-subtitle">
          Diese Karte zusammen mit dem Gerät aufbewahren. Ohne die sechs Wörter lässt sich ein Backup nicht
          mehr entschlüsseln — von niemandem.
        </p>

        <div className="kit-write">
          {KIT_SECRET_LINES.map((label) => (
            <div key={label} className="kit-write-row">
              <span className="kit-write-label">{label}</span>
              <span className="kit-write-line" data-testid={`kit-write:${label}`} />
            </div>
          ))}
        </div>

        <div className="kit-prose">
          <h2>Schritt für Schritt</h2>
          <ol>
            {KIT_RESTORE_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>

        <p className="kit-footer">
          {de.appName} — {de.appTagline}
        </p>
      </section>
    </div>
  );
}
